import { native } from '@forest/native-services'
import { Suspense, useEffect } from 'react'
import { ReminderDaySelectionPage } from '~/features/multi-page/components/reminder-day-selection-page'
import { ReminderPage } from '~/features/multi-page/components/reminder-page'
import { useTrialReminderGroup } from '~/features/multi-page/hooks/use-trial-reminder-group'
import type { TrialReminderGroup } from '~/features/multi-page/lib/trial-reminder'
import { pageVisibleOnce } from '~/loading'

/**
 * Page 3 of the walkthrough. The trial reminder experiment arm decides which
 * reminder page renders and which `page_view` it logs. Only this step waits
 * for native's answer, behind its own boundary so the surrounding layout
 * stays put while it does.
 */
export function ReminderStep(props: { next: () => void; isPremium: boolean }) {
  return (
    <Suspense fallback={null}>
      <ReminderPageForGroup {...props} />
    </Suspense>
  )
}

function ReminderPageForGroup(props: { next: () => void; isPremium: boolean }) {
  const group = useTrialReminderGroup()

  useEffect(() => {
    void pageVisibleOnce.promise.then(() => {
      void native.general.logEvent({
        event_name: 'page_view',
        event_params: {
          page_name: reminderPageViewName(group, props.isPremium),
        },
      })
    })
  }, [group])

  return group === 'Variant1' ? (
    <ReminderDaySelectionPage next={props.next} />
  ) : (
    <ReminderPage next={props.next} />
  )
}

/**
 * Variant1's exposure is counted under one name; Control keeps the existing
 * free/premium split.
 */
function reminderPageViewName(
  group: TrialReminderGroup,
  isPremium: boolean,
): string {
  if (group === 'Variant1') return 'trial_reminder_day_selection'
  return isPremium
    ? 'trial_paywall_trial_remind_premium'
    : 'trial_paywall_trial_remind_free'
}
